import { useState, useEffect } from 'react';
import {
  X, Loader2, Ticket, Send, CheckCircle2, Circle, CalendarClock,
  Users, KeyRound, History, AlertTriangle, Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { getPartnerCouponReport, reshareBatchCouponPartner } from '@/api/stnApi';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

// Resend the onboarding set-password link (+ coupon if still valid) to interns who
// haven't onboarded / used their coupon. Option to extend the coupon deadline.
export default function CouponReshareModal({ batchId, batchName, onClose }) {
  const [data, setData]   = useState(null);
  const [loading, setLoad] = useState(true);
  const [busy, setBusy]   = useState(null);
  const [rowBusy, setRowBusy] = useState(null);
  const [extendDays, setExtendDays] = useState({});
  const [newExpiry, setNewExpiry]   = useState({});
  const [filter, setFilter] = useState('no_temp');

  const load = () => {
    setLoad(true);
    getPartnerCouponReport(batchId)
      .then((r) => setData(r))
      .catch(() => toast.error('Could not load coupon report'))
      .finally(() => setLoad(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [batchId]);

  const buildBody = (code, extra = {}) => {
    const body = { ...extra };
    if (newExpiry[code]) body.newExpiry = newExpiry[code];
    else if (extendDays[code] && Number(extendDays[code]) > 0) body.extendDays = Number(extendDays[code]);
    return body;
  };

  const reshare = async (code) => {
    setBusy(code);
    try {
      const r = await reshareBatchCouponPartner(batchId, buildBody(code));
      toast.success(r?.message || `Reshared to ${r?.sent ?? 0} recipients`);
      setExtendDays(s => ({ ...s, [code]: '' }));
      setNewExpiry(s => ({ ...s, [code]: '' }));
      load();
    } catch (e) { toast.error(e?.message || 'Reshare failed'); }
    finally { setBusy(null); }
  };

  const reshareOne = async (code, studentId) => {
    setRowBusy(studentId);
    try {
      const r = await reshareBatchCouponPartner(batchId, buildBody(code, { recipientIds: [studentId] }));
      toast.success(r?.sent ? 'Onboarding email resent to this intern' : 'Nothing to resend');
      load();
    } catch (e) { toast.error(e?.message || 'Resend failed'); }
    finally { setRowBusy(null); }
  };

  const coupons = data?.coupons || [];
  const visible = (c) => c.recipients.filter(r => filter === 'all' ? true : filter === 'no_temp' ? r.tempPasswordUnused : !r.used);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-0 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-card border border-border w-full max-w-3xl min-h-screen sm:min-h-0 sm:my-6 sm:rounded-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-card z-10 sm:rounded-t-2xl">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-orange-500/10 text-orange-500 flex items-center justify-center shrink-0"><Ticket className="w-4.5 h-4.5" /></div>
            <div className="min-w-0">
              <h3 className="font-bold text-foreground truncate">Onboarding &amp; Coupon Reshare</h3>
              <p className="text-xs text-muted-foreground truncate">{data?.batch?.title || batchName || 'Batch'} · resend the set-password link (+ coupon if valid)</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground shrink-0"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-5">
          {loading ? (
            <div className="flex items-center gap-2 py-12 justify-center text-muted-foreground text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading report…</div>
          ) : coupons.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Ticket className="w-8 h-8 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No bulk coupon was issued for this batch.</p>
            </div>
          ) : coupons.map((c) => {
            const pct = c.issued ? Math.round(c.used / c.issued * 100) : 0;
            const expired = c.expiresAt && new Date(c.expiresAt) < new Date();
            return (
              <div key={c.code} className="rounded-2xl border border-border overflow-hidden">
                <div className="p-4 bg-orange-500/5 border-b border-border">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <span className="font-mono font-extrabold text-lg text-orange-500 tracking-wide">{c.code}</span>
                      <span className="ml-2 text-xs font-semibold text-orange-500/80">{c.discountText} · {c.perStudentLimit} program(s)</span>
                    </div>
                    <span className={cn('text-xs font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1', expired ? 'bg-red-500/10 text-red-500' : c.isActive ? 'bg-emerald-500/10 text-emerald-600' : 'bg-muted text-muted-foreground')}>
                      <CalendarClock className="w-3 h-3" /> {expired ? 'Expired' : 'Expires'} {fmtDate(c.expiresAt)}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3">
                    {[
                      { label: 'Issued',         value: c.issued,        icon: Users,        cls: 'text-foreground' },
                      { label: 'Coupon used',    value: c.used,          icon: CheckCircle2, cls: 'text-emerald-500' },
                      { label: 'Coupon unused',  value: c.unused,        icon: Circle,       cls: 'text-amber-500' },
                      { label: 'Temp pw unused', value: c.notActivated,  icon: KeyRound,     cls: 'text-rose-500' },
                      { label: 'Reshared',       value: c.totalReshares, icon: Send,         cls: 'text-blue-500' },
                    ].map(s => (
                      <div key={s.label} className="bg-card rounded-xl px-3 py-2 border border-border">
                        <s.icon className={cn('w-3.5 h-3.5', s.cls)} />
                        <p className={cn('text-xl font-black mt-1 leading-none', s.cls)}>{s.value}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{s.label}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-3">
                    <div className="h-2 bg-card rounded-full overflow-hidden border border-border">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">{pct}% used · <b className="text-amber-500">{c.unused}</b> still to apply</p>
                  </div>
                </div>

                {/* Reshare action + extend deadline */}
                <div className="p-4 bg-card border-b border-border">
                  <div className="flex items-end gap-2 flex-wrap">
                    <div>
                      <label className="text-[11px] font-semibold text-muted-foreground">Extend deadline by</label>
                      <div className="flex items-center gap-1.5 mt-1">
                        <input type="number" min={0} value={extendDays[c.code] || ''} disabled={!!newExpiry[c.code]}
                          onChange={e => setExtendDays(s => ({ ...s, [c.code]: e.target.value }))}
                          placeholder="0" className="w-20 h-9 rounded-lg border border-border bg-background px-2.5 text-sm disabled:bg-muted/40" />
                        <span className="text-xs text-muted-foreground">days</span>
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground/50 pb-2.5">or</span>
                    <div>
                      <label className="text-[11px] font-semibold text-muted-foreground">New expiry date</label>
                      <input type="date" value={newExpiry[c.code] || ''}
                        onChange={e => setNewExpiry(s => ({ ...s, [c.code]: e.target.value }))}
                        className="block mt-1 h-9 rounded-lg border border-border bg-background px-2.5 text-sm" />
                    </div>
                    <button disabled={busy === c.code || c.unused === 0} onClick={() => reshare(c.code)}
                      className="ml-auto h-9 px-4 rounded-lg bg-orange-500 text-white text-xs font-bold hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-1.5">
                      {busy === c.code ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                      Reshare to {c.unused} unused
                    </button>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-2">
                    Interns who haven't set a password get a fresh set-password link{!expired ? ' with the coupon' : ' — the expired coupon is not re-sent unless you extend it'}.
                  </p>
                </div>

                {c.reshareHistory?.length > 0 && (
                  <div className="px-4 py-2.5 bg-muted/30 border-b border-border">
                    <p className="text-[11px] font-semibold text-muted-foreground flex items-center gap-1 mb-1"><History className="w-3 h-3" /> Reshare history</p>
                    <div className="flex flex-wrap gap-1.5">
                      {c.reshareHistory.slice().reverse().map((h, i) => (
                        <span key={i} className="text-[10px] bg-card border border-border rounded-full px-2 py-0.5 text-muted-foreground">
                          {fmtDateTime(h.at)} · {h.recipientCount} sent{h.extendedTo ? ` · → ${fmtDate(h.extendedTo)}` : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recipient report — filterable */}
                <div className="px-4 pt-3 flex items-center gap-1.5 flex-wrap">
                  {[
                    { key: 'no_temp', label: `Not set password (${c.notActivated})` },
                    { key: 'unused',  label: `Coupon unused (${c.unused})` },
                    { key: 'all',     label: `All (${c.issued})` },
                  ].map((t) => (
                    <button key={t.key} onClick={() => setFilter(t.key)}
                      className={cn('text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors', filter === t.key ? 'bg-orange-500 text-white border-orange-500' : 'bg-card text-muted-foreground border-border hover:border-orange-500/40')}>
                      {t.label}
                    </button>
                  ))}
                  {expired && c.unused > 0 && (
                    <span className="ml-auto text-[11px] text-red-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Extend the deadline before resharing.</span>
                  )}
                </div>
                <div className="max-h-72 overflow-y-auto mt-2">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 sticky top-0">
                      <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                        <th className="px-4 py-2">Recipient</th>
                        <th className="px-2 py-2 text-center">Temp pw / Account</th>
                        <th className="px-2 py-2 text-center">Coupon</th>
                        <th className="px-2 py-2 text-center">Reshared</th>
                        <th className="px-2 py-2 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/60">
                      {visible(c).map((r) => (
                        <tr key={r.studentId} className="hover:bg-muted/40">
                          <td className="px-4 py-2">
                            <p className="font-semibold text-foreground">{r.name}</p>
                            <p className="text-[10px] text-muted-foreground">{r.email}{r.mobile ? ` · ${r.mobile}` : ''}</p>
                          </td>
                          <td className="px-2 py-2 text-center">
                            {r.accountSetup
                              ? <span className="text-[10px] font-semibold text-emerald-500 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Set up</span>
                              : <span className="text-[10px] font-semibold text-rose-500 inline-flex items-center gap-1"><KeyRound className="w-3 h-3" /> Not used</span>}
                          </td>
                          <td className="px-2 py-2 text-center">
                            {r.used
                              ? <span className="text-[10px] font-bold text-emerald-500">Used</span>
                              : <span className="text-[10px] font-semibold text-amber-500">Not used</span>}
                          </td>
                          <td className="px-2 py-2 text-center text-muted-foreground">{r.reshareCount > 0 ? `${r.reshareCount}×` : '—'}</td>
                          <td className="px-2 py-2 text-right">
                            {!r.used && (
                              <button onClick={() => reshareOne(c.code, r.studentId)} disabled={rowBusy === r.studentId}
                                title="Resend onboarding email to this intern"
                                className="h-7 px-2 inline-flex items-center gap-1 rounded-lg border border-orange-500/30 text-orange-500 hover:bg-orange-500/10 text-[10px] font-semibold disabled:opacity-50">
                                {rowBusy === r.studentId ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />} Resend
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {visible(c).length === 0 && (
                        <tr><td colSpan={5} className="text-center py-6 text-muted-foreground">No recipients in this view.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
